import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import { DEFAULT_POLL_INTERVAL_MS } from '../../../hooks/useDriverLocationsPoll';

const RING_SIZE = 22;
const RING_RADIUS = 9;
const RING_CIRC = 2 * Math.PI * RING_RADIUS; // ~56.55

const StyledDiv = styledComponent('div', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: theme.sizes.captionFontSize,
  color: theme.colors.textMuted,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',

  '& .pc-svg': {
    display: 'block',
  },
  '& .pc-track': {
    stroke: theme.colors.borderLight,
  },
  '& .pc-progress': {
    stroke: theme.colors.statusOnTrack,
    transition: 'stroke-dashoffset 1s linear',
  },
  '& .pc-progress.refreshing': {
    stroke: theme.colors.statusBehind,
  },
});

// Computes seconds since lastPolledAt clamped to [0, intervalSec].
function secondsElapsed(lastPolledAt, now, intervalSec) {
  if (!lastPolledAt) return intervalSec;
  const elapsedMs = now.getTime() - lastPolledAt.getTime();
  const elapsedSec = Math.floor(elapsedMs / 1000);
  return Math.max(0, Math.min(intervalSec, elapsedSec));
}

function PollCountdown({ lastPolledAt, intervalMs, isLoading }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const intervalSec = Math.max(1, Math.round(intervalMs / 1000));
  const elapsed = secondsElapsed(lastPolledAt, now, intervalSec);
  const remaining = Math.max(0, intervalSec - elapsed);
  const progressFraction = elapsed / intervalSec;
  const dashOffset = RING_CIRC * (1 - progressFraction);

  const label = isLoading ? 'Updating now…' : `Next update in ${remaining}s`;

  return (
    <StyledDiv aria-live="polite">
      <svg
        className="pc-svg"
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        aria-hidden="true"
      >
        <circle
          className="pc-track"
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          strokeWidth="2"
        />
        <circle
          className={`pc-progress ${isLoading ? 'refreshing' : ''}`}
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={RING_CIRC}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
        />
      </svg>
      <span>{label}</span>
    </StyledDiv>
  );
}

PollCountdown.propTypes = {
  lastPolledAt: PropTypes.instanceOf(Date),
  intervalMs: PropTypes.number,
  isLoading: PropTypes.bool,
};

PollCountdown.defaultProps = {
  lastPolledAt: null,
  intervalMs: DEFAULT_POLL_INTERVAL_MS,
  isLoading: false,
};

export default PollCountdown;
